import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import CodeIcon from '@mui/icons-material/Code';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { useOptionalAuth } from 'contexts/AuthContext';

import LabelChip from 'components/chip/LabelChip';
import VisibilityChip from 'components/chip/VisibilityChip';
import DeleteConfirmDialog from 'components/dialog/DeleteConfirmDialog';
import { KnzResolvedFile } from 'components/knz/KnzFileResolverContext';
import KnzTocSidebar from 'components/knz/KnzTocSidebar';
import KnzViewer, { KnzTocItem } from 'components/knz/KnzViewer';
import NotFound from 'pages/error/NotFound';
import { NotFoundError } from 'stores/ApiClientError';
import {
  ArticleDetail as ArticleDetailType,
  deleteArticle,
  getArticle,
  getPublicArticleFile,
} from 'stores/ArticleStoreAdapter';
import { getFileDetail } from 'stores/WorkSpaceStoreAdapter';
import { formatDate } from 'utils/DateTimeFormatter';
import { errorDefault, successDefault, toErrorDetail } from 'utils/Toastify';

import MarkdownViewer from '../components/MarkdownViewer';
import { formatAuthors, formatReadTime } from '../components/articleUtils';
import RecommendedArticles from './RecommendedArticles';

/** 本文の表示モード（レンダリング済み / raw ソース） */
type BodyViewMode = 'rendered' | 'raw';

/**
 * 本文の raw ソース表示（Prism ハイライト + コピー）。
 * knz / md いずれも markdown 言語として色付けする。ダーク背景固定で視認性を確保する。
 */
const RawSourceView: React.FC<{ source: string }> = ({ source }) => {
  const [copied, setCopied] = React.useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(source);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Box sx={{ borderRadius: 2, overflow: 'hidden', border: 1, borderColor: '#2d333b' }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 1.5, py: 0.75, bgcolor: '#1c2128', borderBottom: 1, borderColor: '#2d333b' }}
      >
        <Typography variant="caption" fontWeight={600} sx={{ color: '#768390' }}>
          raw
        </Typography>
        <Tooltip title={copied ? 'コピーしました' : 'コピー'}>
          <IconButton size="small" onClick={handleCopy} sx={{ p: 0.5, color: '#768390' }}>
            <ContentCopyIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </Stack>
      <SyntaxHighlighter
        language="markdown"
        style={oneDark}
        customStyle={{ margin: 0, padding: '12px', fontSize: '0.78rem', background: '#0d1117' }}
        wrapLongLines
      >
        {source}
      </SyntaxHighlighter>
    </Box>
  );
};

/**
 * 記事詳細（閲覧）画面。ゲストでもアクセス可能。
 * メタヘッダー（API6202）+ 本文（API6111 で detail 取得、knz/md で描画を出し分け）。
 */
const ArticleDetail: React.FC = () => {
  const { articleId } = useParams<{ articleId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { userSession } = useOptionalAuth();
  const isLoggedIn = userSession !== null;

  const [article, setArticle] = React.useState<ArticleDetailType | null>(null);
  const [body, setBody] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [notFound, setNotFound] = React.useState(false);
  const [bodyError, setBodyError] = React.useState(false);
  const [deleteOpen, setDeleteOpen] = React.useState(false);
  // 本文（knz）に Toc ブロックがある場合に、収集した目次項目を保持する（右カラム目次用）。
  const [tocItems, setTocItems] = React.useState<KnzTocItem[]>([]);
  // 本文（knz）に ArticleNav がある場合の有無・下部スロット高さ・表示ID / 溢れID
  // （末尾スペーサー調整 / おすすめ重複除外 / 溢れ分の先頭表示用）。
  const [articleNav, setArticleNav] = React.useState<{
    present: boolean;
    bottomHeight: number;
    displayedIds: string[];
    overflowIds: string[];
  }>({
    present: false,
    bottomHeight: 0,
    displayedIds: [],
    overflowIds: [],
  });
  // おすすめ記事の表示件数（末尾余白の調整用。0 のときは欄が出ていない）。
  const [recommendedCount, setRecommendedCount] = React.useState(0);
  // 本文の表示モード（レンダリング済み / raw ソース）。
  const [bodyView, setBodyView] = React.useState<BodyViewMode>('rendered');

  /**
   * 本文中の外部ファイル参照（CodeBlock の link 等）の解決関数。
   * まず公開用API（API6111）で取得し、記事化されていない参照先で 404 になった場合は、
   * ログイン済みなら認証必須のファイル詳細取得（getFileDetail）にフォールバックする。
   * これにより、記事の著者は「まだ記事化していない自分のファイル」を参照しても本文内で表示できる。
   * ゲストは公開用APIのみ（該当なしは従来どおり表示できない）。
   */
  const resolveFile = React.useCallback(
    async (fileId: string): Promise<KnzResolvedFile> => {
      try {
        return await getPublicArticleFile(fileId);
      } catch (e) {
        if (isLoggedIn) {
          // 公開記事として存在しない fileId でも、ログイン済み（＝自分のファイル権限があれば）取得を試みる
          const detail = await getFileDetail(fileId);
          return {
            fileId: detail.fileId,
            extension: detail.extension,
            detail: detail.detail,
            name: detail.name,
            path: detail.path,
          };
        }
        throw e;
      }
    },
    [isLoggedIn],
  );

  React.useEffect(() => {
    if (!articleId) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    setBodyError(false);
    setBody(null);
    setTocItems([]);
    setBodyView('rendered');

    getArticle(articleId)
      .then(async (detail) => {
        if (cancelled) return;
        setArticle(detail);
        // 本文取得（公開用ファイル）
        try {
          const file = await getPublicArticleFile(detail.fileId);
          if (!cancelled) setBody(file.detail);
        } catch (e) {
          if (!cancelled) setBodyError(true);
          // eslint-disable-next-line no-console
          console.error(e);
        }
      })
      .catch((e) => {
        if (cancelled) return;
        if (e instanceof NotFoundError) {
          setNotFound(true);
        } else {
          errorDefault('記事の取得に失敗しました');
          // eslint-disable-next-line no-console
          console.error(e);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [articleId]);

  // URL に #anchor（ArticleLinkCard の section 指定などで付与）があれば、本文描画後に
  // 該当 Section（Section は anchor を要素 id として描画する）へスクロールする。
  // 本文は非同期に描画されるため、body がセットされ rendered 表示のときに実行する。
  // 描画完了とのタイミングずれを吸収するため、少し待ってから getElementById する。
  React.useEffect(() => {
    const hash = decodeURIComponent(location.hash.replace(/^#/, ''));
    if (!hash || body === null || bodyView !== 'rendered') return;
    let raf = 0;
    const timer = window.setTimeout(() => {
      raf = window.requestAnimationFrame(() => {
        document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }, 120);
    return () => {
      window.clearTimeout(timer);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, [location.hash, body, bodyView]);

  const handleDelete = async () => {
    if (!article) return;
    try {
      await deleteArticle({ articleId: article.articleId });
      successDefault('記事を削除しました');
      navigate('/devdocs');
    } catch (e) {
      errorDefault('記事の削除に失敗しました', toErrorDetail(e));
      // eslint-disable-next-line no-console
      console.error(e);
    }
  };

  if (notFound) {
    return <NotFound />;
  }

  if (loading || !article) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  const readTime = formatReadTime(article.charCount);
  const authorText = formatAuthors(article.authors);
  const canEdit = isLoggedIn && article.editable;
  const isKnz = article.extension === 'knz';
  // Toc ブロックを含む knz 記事のときだけ右カラム目次を出す。
  // raw 表示中は本文をレンダリングしないため目次も出さない。
  const showToc = isKnz && bodyView === 'rendered' && tocItems.length > 0;

  const main = (
    <Stack spacing={2.5} sx={{ maxWidth: 860, width: '100%', mr: 'auto' }}>
      {/* 戻る + 編集導線 */}
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Button
          size="small"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/devdocs')}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 2,
            color: 'text.secondary',
            '&:hover': { bgcolor: 'action.hover', color: 'primary.main' },
          }}
        >
          一覧に戻る
        </Button>
        {canEdit && (
          <Stack direction="row" spacing={1}>
            {isKnz && (
              <Tooltip title="ワークスペースで knz ファイルを直接編集（別タブ）">
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<OpenInNewIcon />}
                  onClick={() =>
                    window.open(
                      `/workspace?fileId=${encodeURIComponent(article.fileId)}`,
                      '_blank',
                      'noopener,noreferrer',
                    )
                  }
                  sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
                >
                  ワークスペースで編集
                </Button>
              </Tooltip>
            )}
            <Button
              size="small"
              variant="outlined"
              startIcon={<EditOutlinedIcon />}
              onClick={() => navigate(`/devdocs/article/${article.articleId}/edit`)}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
            >
              編集画面へ
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={<DeleteOutlineIcon />}
              onClick={() => setDeleteOpen(true)}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
            >
              削除
            </Button>
          </Stack>
        )}
      </Stack>

      {/* メタヘッダー（スタイリッシュ） */}
      <Box>
        {/* ラベル + 公開設定 */}
        <Stack direction="row" spacing={0.5} sx={{ mb: 1.5, flexWrap: 'wrap', gap: 0.5 }}>
          {article.labels.map((l) => (
            <LabelChip key={l.labelId} name={l.name} color={l.color} />
          ))}
          <VisibilityChip visibility={article.visibility} />
        </Stack>

        {/* タイトル（大きすぎないよう h5） */}
        <Typography
          variant="h5"
          fontWeight={800}
          sx={{ letterSpacing: '-0.01em', lineHeight: 1.35 }}
        >
          {article.title}
        </Typography>

        {/* サブタイトル */}
        {article.subtitle && (
          <Typography
            variant="subtitle1"
            color="text.secondary"
            sx={{ mt: 0.5, fontWeight: 500, lineHeight: 1.5 }}
          >
            {article.subtitle}
          </Typography>
        )}

        {/* 説明（リード文） */}
        {article.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.25, lineHeight: 1.85 }}>
            {article.description}
          </Typography>
        )}

        {/* メタ行（左: 著者・公開日・読了目安 / 右: 本文表示モード切替） */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mt: 2, gap: 1, flexWrap: 'wrap', rowGap: 0.5 }}
        >
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ color: 'text.secondary', flexWrap: 'wrap', rowGap: 0.5 }}
          >
            {authorText && (
              <Stack direction="row" spacing={0.5} alignItems="center">
                <PersonOutlineIcon sx={{ fontSize: 15, color: 'text.disabled' }} />
                <Typography variant="caption" fontWeight={600}>
                  {authorText}
                </Typography>
              </Stack>
            )}
            {authorText && (
              <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: 'text.disabled' }} />
            )}
            <Typography variant="caption">{formatDate(article.createdAt)}</Typography>
            {readTime && (
              <>
                <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: 'text.disabled' }} />
                <Typography variant="caption">{readTime}</Typography>
              </>
            )}
          </Stack>

          {/* 本文の表示モード切替（本文が取得できているときだけ出す） */}
          {body !== null && !bodyError && (
            <ToggleButtonGroup
              size="small"
              exclusive
              value={bodyView}
              onChange={(_, next) => {
                if (next) setBodyView(next as BodyViewMode);
              }}
              sx={{
                '& .MuiToggleButton-root': {
                  px: 1,
                  py: 0.25,
                  border: 1,
                  borderColor: 'divider',
                  textTransform: 'none',
                },
              }}
            >
              <ToggleButton value="rendered">
                <Tooltip title="レンダリング表示">
                  <ArticleOutlinedIcon fontSize="small" />
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="raw">
                <Tooltip title="raw テキスト表示">
                  <CodeIcon fontSize="small" />
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
          )}
        </Stack>
      </Box>

      <Divider />

      {/* 本文 */}
      {bodyError ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
          本文を取得できませんでした。
        </Typography>
      ) : body === null ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={24} />
        </Box>
      ) : bodyView === 'raw' ? (
        <Box sx={{ pb: 2 }}>
          <RawSourceView source={body} />
        </Box>
      ) : isKnz ? (
        <KnzViewer
          source={body}
          hideHeader
          fetchFile={resolveFile}
          tocMode="hoist"
          onTocChange={setTocItems}
          articleNavContext="article"
          onArticleNavChange={setArticleNav}
        />
      ) : (
        <Box sx={{ pb: 2 }}>
          <MarkdownViewer source={body} />
        </Box>
      )}

      {/*
        おすすめ記事（記事詳細下部）。
        当該記事のラベルを持つ記事を関連度順に並べる。ラベル 0 件 / 該当なしのときは欄自体を出さない。
        本文の ArticleNav（前後・関連記事）に既出の記事は重複を避けて除外する。
        表示件数を recommendedCount で受け取り、末尾スペーサーの高さ調整に使う。
        左右のスペースは「関連する記事」（KnzViewer 内の ArticleNav）と揃えるため、
        KnzViewer の本文ラッパ（px: 3 / maxWidth: 900 / mx: auto）と同じ枠に入れる。
      */}
      <Box sx={{ px: 3, maxWidth: 900, mx: 'auto', width: '100%' }}>
        <RecommendedArticles
          articleId={article.articleId}
          labels={article.labels}
          excludeArticleIds={articleNav.present ? articleNav.displayedIds : undefined}
          pinnedArticleIds={articleNav.present ? articleNav.overflowIds : undefined}
          onCountChange={setRecommendedCount}
        />
      </Box>

      {/*
        記事末尾の余白（スペーサー）。
        下部が「おすすめ記事」や ArticleNav（前後・関連記事ナビ）で埋まる分だけ 80vh から差し引く
        （余白の出過ぎ防止）。差し引きすぎないよう下限 16vh。
        - ArticleNav の下部スロット実高さ（bottomHeight）
        - おすすめ記事の件数に応じた概算高さ（見出し + カード行）
        いずれも無ければ従来どおり 80vh。
      */}
      {(() => {
        // おすすめ記事の概算高さ（px）。1 行 3 枚（レスポンシブで折り返し）想定でカード高さ ~120px、
        // 見出し・余白ぶんを加味した控えめな概算。0 件なら 0。
        const RECOMMENDED_HEADER = 48;
        const RECOMMENDED_ROW = 132;
        const perRow = 3;
        const recommendedHeight =
          recommendedCount > 0
            ? RECOMMENDED_HEADER + Math.ceil(recommendedCount / perRow) * RECOMMENDED_ROW
            : 0;
        const filledHeight = (articleNav.present ? articleNav.bottomHeight : 0) + recommendedHeight;
        return (
          <Box
            sx={{
              height: filledHeight > 0 ? `max(16vh, calc(80vh - ${filledHeight}px))` : '80vh',
              flexShrink: 0,
            }}
            aria-hidden
          />
        );
      })()}

      <DeleteConfirmDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDelete}
        title="記事の削除"
        confirmLabel="削除"
      >
        <Typography variant="body2">
          「{article.title}」を削除します。この操作は取り消せません。
        </Typography>
      </DeleteConfirmDialog>
    </Stack>
  );

  // Toc がある knz 記事のみ、記事のすぐ右に scrollspy 目次を出す 2 カラムレイアウト。
  // 目次が無ければ従来どおり本文のみ（左寄せ）。
  if (!showToc) {
    return main;
  }

  // 記事本体は従来どおり左寄せのまま。空いている右側の余白に目次を出す。
  // 目次は記事のすぐ右（本体 860 の直後）に置き、スクロールコンテナに sticky で
  // 張り付いて位置が固定される。
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 4 }}>
      <Box sx={{ maxWidth: 860, width: '100%', flexShrink: 1, minWidth: 0 }}>{main}</Box>
      <Box
        sx={{
          width: 300,
          flexShrink: 0,
          alignSelf: 'flex-start',
          position: 'sticky',
          top: 8,
          display: { xs: 'none', lg: 'block' },
        }}
      >
        <KnzTocSidebar items={tocItems} />
      </Box>
    </Box>
  );
};

export default ArticleDetail;
